import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { EditModal, MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type {
  MarketPrice,
  StaffProfileStoresItem,
  StoreProduct,
  StoreVariantPricing,
  Variant,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { toMajorString } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { marketKeys, useMarkets } from '@/features/markets';
import { session } from '@/shared/session/session';
import { useStoreOrNull } from '@/shared/store/store-context';

import { storeCatalogApi } from '../api/store-catalog-api';
import { storeCatalogKeys } from '../hooks/store-catalog-keys';
import { marketPricesQueryOptions } from '../hooks/use-market-prices';
import { storePricingQueryOptions } from '../hooks/use-store-pricing';
import { storeMembershipQueryOptions } from '../hooks/use-store-products';
import { marketEditKind, writeMarketEdit } from '../hooks/use-variant-market-drafts';
import { draftFromEntry, type MarketPriceDraft } from '../schemas/market-price-rules';
import {
  draftFromRow,
  isDraftDirty,
  priceSource,
  toPricingBody,
  type PricingDraft,
} from '../schemas/pricing-rules';

import { PriceSourceBadge } from './price-source-badge';
import { StoreProductStatusBadge } from './store-product-status-badge';

const EDITED = 'border-primary bg-primary/5';

export interface StorePricesModalProps {
  productId: string;
  variant: Pick<Variant, 'id' | 'title' | 'price'>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface MarketEdit {
  storeId: string;
  marketId: string;
  draft: MarketPriceDraft;
  entry: MarketPrice | undefined;
}

const marketEditKey = (storeId: string, marketId: string) => `${storeId}:${marketId}`;

function StoreStateChip({ membership }: { membership: StoreProduct | null }) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  if (!membership) {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        {t('variantPage2.storePrices.state.notAdded')}
      </Badge>
    );
  }
  if (membership.publishAt) {
    return (
      <Badge
        variant="outline"
        className="bg-warning/20 text-warning-foreground dark:text-warning border-transparent"
      >
        {format.date(membership.publishAt)}
      </Badge>
    );
  }
  if (membership.status === 'published') {
    return (
      <Badge
        variant="outline"
        className="bg-success/15 text-success dark:bg-success/20 border-transparent"
      >
        {t('variantPage2.storePrices.state.live')}
      </Badge>
    );
  }
  return <StoreProductStatusBadge status={membership.status} />;
}

interface MarketsSectionProps {
  store: StaffProfileStoresItem;
  productId: string;
  variantId: string;
  row: StoreVariantPricing | undefined;
  canWrite: boolean;
  edits: ReadonlyMap<string, MarketEdit>;
  onEdit: (edit: MarketEdit) => void;
}

function MarketsSection({
  store,
  productId,
  variantId,
  row,
  canWrite,
  edits,
  onEdit,
}: MarketsSectionProps) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  const markets = useMarkets(store.id);
  const list = markets.data ?? [];
  const prices = useQueries({
    queries: list.map((market) => marketPricesQueryOptions(store.id, market.id, productId)),
  });

  return (
    <section className="flex flex-col gap-2.5">
      <h3 className="text-sm font-semibold">
        {t('variantPage2.storePrices.marketsIn', { store: store.name })}
      </h3>
      {markets.isPending ? (
        <Skeleton className="h-24" />
      ) : markets.error ? (
        <ErrorState error={markets.error} onRetry={() => void markets.refetch()} compact />
      ) : list.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('marketPrices.noMarkets')}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table aria-label={t('variantPage2.storePrices.marketsIn', { store: store.name })}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('variantPage2.storePrices.columns.market')}</TableHead>
                <TableHead className="text-right">
                  {t('variantPage2.storePrices.columns.price')}
                </TableHead>
                <TableHead>{t('variantPage2.storePrices.columns.source')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((market, index) => {
                const name = t('variantPage2.marketPrice', {
                  name: market.name,
                  currency: market.currency,
                });
                if (market.isDefault) {
                  return (
                    <TableRow key={market.id}>
                      <TableCell>{name}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row?.effective ? format.money(row.effective) : '—'}
                      </TableCell>
                      <TableCell>
                        {row ? t(`variantPage2.storePrices.source.${priceSource(row)}`) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                }
                const entry = prices[index]?.data?.items.find(
                  (item) => item.variantId === variantId,
                );
                const edit = edits.get(marketEditKey(store.id, market.id));
                const draft = edit?.draft ?? draftFromEntry(entry);
                const edited = edit ? marketEditKind(edit.draft, entry) !== 'none' : false;
                return (
                  <TableRow key={market.id}>
                    <TableCell>{name}</TableCell>
                    <TableCell className="w-44 text-right">
                      <MoneyInput
                        currency={market.currency}
                        value={draft.price}
                        disabled={!canWrite || prices[index]?.isPending}
                        placeholder={t('variantPage2.automatic')}
                        aria-label={t('variantPage2.storePrices.marketPriceFor', {
                          market: market.name,
                        })}
                        className={cn('h-8', edited && EDITED)}
                        onChange={(price) =>
                          onEdit({
                            storeId: store.id,
                            marketId: market.id,
                            draft: { ...draft, price, compareAt: price ? draft.compareAt : null },
                            entry,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {entry ? t('marketPrices.fixed') : t('pricing.source.converted')}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

function Body({ productId, variant, onOpenChange }: Omit<StorePricesModalProps, 'open'>) {
  const { t } = useTranslation(['storeCatalog', 'common', 'errors']);
  const format = useFormat();
  const message = useErrorMessage();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can('products.write');
  const { me } = session.useSession();
  const stores = useMemo(() => me?.stores ?? [], [me]);
  const currentStore = useStoreOrNull();

  const memberships = useQueries({
    queries: stores.map((store) => storeMembershipQueryOptions(store.id, productId)),
  });
  const pricing = useQueries({
    queries: stores.map((store, index) => {
      const membership = memberships[index]?.data;
      return {
        ...storePricingQueryOptions(store.id, membership?.id ?? ''),
        enabled: Boolean(membership),
      };
    }),
  });
  const rows = stores.map((store, index) => {
    const membership = memberships[index];
    const prices = pricing[index];
    return {
      store,
      membership: membership?.data ?? null,
      row: prices?.data?.find((item) => item.variantId === variant.id),
      isPending: membership?.isPending === true || (membership?.data && prices?.isPending),
      error: membership?.error ?? prices?.error ?? null,
      refetch: () => {
        void (membership?.error ? membership.refetch() : prices?.refetch());
      },
    };
  });

  const [priceDrafts, setPriceDrafts] = useState<Map<string, PricingDraft>>(new Map());
  const [marketEdits, setMarketEdits] = useState<Map<string, MarketEdit>>(new Map());
  const [storeErrors, setStoreErrors] = useState<Map<string, string>>(new Map());
  const [pickedStoreId, setPickedStoreId] = useState<string | null>(null);

  const added = rows.filter((entry) => entry.membership);
  const marketsStore =
    added.find((entry) => entry.store.id === pickedStoreId) ??
    added.find((entry) => entry.store.id === currentStore?.id) ??
    added[0];

  const clearStoreError = (storeId: string) =>
    setStoreErrors((current) => {
      if (!current.has(storeId)) return current;
      const next = new Map(current);
      next.delete(storeId);
      return next;
    });

  const priceChanges = rows.flatMap(({ store, row }) => {
    const draft = priceDrafts.get(store.id);
    return row && draft && isDraftDirty(draft, row) ? [{ storeId: store.id, draft }] : [];
  });
  const marketChanges = [...marketEdits].filter(
    ([, edit]) => marketEditKind(edit.draft, edit.entry) !== 'none',
  );
  const changeCount = priceChanges.length + marketChanges.length;

  const save = useMutation({
    mutationFn: async () => {
      const writes = [
        ...priceChanges.map(({ storeId, draft }) => ({
          storeId,
          forget: () =>
            setPriceDrafts((current) => {
              const next = new Map(current);
              next.delete(storeId);
              return next;
            }),
          run: () => storeCatalogApi.variantPricing(storeId, variant.id, toPricingBody(draft)),
        })),
        ...marketChanges.map(([key, edit]) => ({
          storeId: edit.storeId,
          forget: () =>
            setMarketEdits((current) => {
              const next = new Map(current);
              next.delete(key);
              return next;
            }),
          run: () =>
            writeMarketEdit(edit.storeId, edit.marketId, variant.id, edit.draft, edit.entry),
        })),
      ];
      const results = await Promise.allSettled(writes.map((write) => write.run()));

      await Promise.all(
        [...new Set(writes.map((write) => write.storeId))].flatMap((storeId) => {
          const membership = rows.find((entry) => entry.store.id === storeId)?.membership;
          return [
            ...(membership
              ? [
                  queryClient.invalidateQueries({
                    queryKey: storeCatalogKeys.pricing(storeId, membership.id),
                  }),
                ]
              : []),
            queryClient.invalidateQueries({
              queryKey: [...storeCatalogKeys.all(storeId), 'market-prices'],
            }),
            queryClient.invalidateQueries({ queryKey: marketKeys.list(storeId) }),
          ];
        }),
      );

      const errors = new Map<string, string>();
      results.forEach((result, index) => {
        const write = writes[index];
        if (!write) return;
        if (result.status === 'fulfilled') {
          write.forget();
          return;
        }
        const reason = result.reason as unknown;
        const reference =
          isApiError(reason) && reason.requestId
            ? ` ${t('errors:requestIdShort', { requestId: reason.requestId })}`
            : '';
        if (!errors.has(write.storeId)) {
          errors.set(write.storeId, `${message(reason)}${reference}`);
        }
      });
      setStoreErrors(errors);
      // Thrown only so the "saved" toast and the close wait for every store; the rows say why.
      if (errors.size > 0) throw new Error('Some stores could not be saved');
    },
    meta: {
      successMessage: 'storeCatalog:variantPage2.storePrices.saved',
      silentError: true,
    },
    onSuccess: () => onOpenChange(false),
  });

  const failedNames = rows
    .filter((entry) => storeErrors.has(entry.store.id))
    .map((entry) => entry.store.name);

  return (
    <EditModal
      open
      onOpenChange={onOpenChange}
      size="lg"
      title={t('storeCatalog:variantPage2.storePrices.title')}
      description={t('storeCatalog:variantPage2.storePrices.description', {
        variant: variant.title,
        price: format.money(variant.price),
      })}
      footerStart={
        <span className="text-muted-foreground text-[13px]">
          {t('storeCatalog:variantPage2.storePrices.savedStraightAway')}
        </span>
      }
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common:actions.cancel')}
          </Button>
          {canWrite ? (
            <Button
              type="button"
              disabled={changeCount === 0 || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending
                ? t('common:actions.saving')
                : t('storeCatalog:variantPage2.storePrices.save')}
            </Button>
          ) : null}
        </>
      }
    >
      {failedNames.length > 0 ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            {t('storeCatalog:variantPage2.storePrices.partial', {
              count: failedNames.length,
              store: failedNames[0],
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      <section className="flex flex-col gap-2.5">
        <div>
          <h3 className="text-sm font-semibold">
            {t('storeCatalog:variantPage2.storePrices.stores')}
          </h3>
          <p className="text-muted-foreground text-[13px]">
            {t('storeCatalog:variantPage2.storePrices.storesHint')}
          </p>
        </div>
        <div className="overflow-x-auto rounded-lg border">
          <Table aria-label={t('storeCatalog:variantPage2.storePrices.stores')}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('storeCatalog:variantPage2.storePrices.columns.store')}</TableHead>
                <TableHead className="text-right">
                  {t('storeCatalog:variantPage2.storePrices.columns.price')}
                </TableHead>
                <TableHead className="text-right">
                  {t('storeCatalog:variantPage2.storePrices.columns.compareAt')}
                </TableHead>
                <TableHead>{t('storeCatalog:variantPage2.storePrices.columns.source')}</TableHead>
                <TableHead>{t('storeCatalog:variantPage2.storePrices.columns.state')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(({ store, membership, row, isPending, error, refetch }) => {
                const picked = marketsStore?.store.id === store.id;
                const name = store.name;
                const failure = storeErrors.get(store.id);
                if (isPending) {
                  return (
                    <TableRow key={store.id}>
                      <TableCell>{name}</TableCell>
                      <TableCell colSpan={4}>
                        <Skeleton className="h-8" />
                      </TableCell>
                    </TableRow>
                  );
                }
                if (error) {
                  return (
                    <TableRow key={store.id}>
                      <TableCell>{name}</TableCell>
                      <TableCell colSpan={4}>
                        <ErrorState error={error} onRetry={refetch} compact />
                      </TableCell>
                    </TableRow>
                  );
                }
                if (!membership || !row) {
                  return (
                    <TableRow key={store.id}>
                      <TableCell>{name}</TableCell>
                      <TableCell className="text-muted-foreground text-right">
                        {t('storeCatalog:variantPage2.storePrices.notInStore')}
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell>
                        <StoreStateChip membership={membership} />
                      </TableCell>
                    </TableRow>
                  );
                }
                const saved = draftFromRow(row);
                const draft = priceDrafts.get(store.id) ?? saved;
                const setDraft = (next: PricingDraft) => {
                  setPriceDrafts((current) => new Map(current).set(store.id, next));
                  clearStoreError(store.id);
                };
                return (
                  <TableRow
                    key={store.id}
                    data-state={picked ? 'selected' : undefined}
                    className="cursor-pointer"
                    onClick={() => setPickedStoreId(store.id)}
                  >
                    <TableCell className="min-w-36">
                      <button
                        type="button"
                        aria-pressed={picked}
                        aria-label={t('storeCatalog:variantPage2.storePrices.pickStore', {
                          store: name,
                        })}
                        className="text-left hover:underline"
                        onClick={() => setPickedStoreId(store.id)}
                      >
                        {name}
                      </button>
                      {failure ? (
                        <span role="alert" className="text-destructive block text-xs">
                          {failure}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="w-44">
                      <MoneyInput
                        currency={store.defaultCurrency}
                        value={draft.price}
                        disabled={!canWrite}
                        placeholder={row.effective ? toMajorString(row.effective) : undefined}
                        aria-label={t('storeCatalog:variantPage2.storePrices.priceFor', {
                          store: name,
                        })}
                        className={cn(
                          'h-8',
                          (draft.price?.amount ?? null) !== (saved.price?.amount ?? null) && EDITED,
                        )}
                        onChange={(price) =>
                          setDraft({ ...draft, price, compareAt: price ? draft.compareAt : null })
                        }
                      />
                    </TableCell>
                    <TableCell className="w-44">
                      <MoneyInput
                        currency={store.defaultCurrency}
                        value={draft.compareAt}
                        disabled={!canWrite || !draft.price}
                        placeholder={
                          row.effectiveCompareAt ? toMajorString(row.effectiveCompareAt) : undefined
                        }
                        aria-label={t('storeCatalog:variantPage2.storePrices.compareAtFor', {
                          store: name,
                        })}
                        className={cn(
                          'h-8',
                          (draft.compareAt?.amount ?? null) !== (saved.compareAt?.amount ?? null) &&
                            EDITED,
                        )}
                        onChange={(compareAt) => setDraft({ ...draft, compareAt })}
                      />
                    </TableCell>
                    <TableCell>
                      <PriceSourceBadge
                        source={priceSource(row)}
                        label={t(
                          `storeCatalog:variantPage2.storePrices.source.${priceSource(row)}`,
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <StoreStateChip membership={membership} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>

      {marketsStore ? (
        <MarketsSection
          key={marketsStore.store.id}
          store={marketsStore.store}
          productId={productId}
          variantId={variant.id}
          row={marketsStore.row}
          canWrite={canWrite}
          edits={marketEdits}
          onEdit={(edit) => {
            setMarketEdits((current) =>
              new Map(current).set(marketEditKey(edit.storeId, edit.marketId), edit),
            );
            clearStoreError(edit.storeId);
          }}
        />
      ) : null}
    </EditModal>
  );
}

/**
 * One variant's price in every store and in one store's markets at a time. An action modal: it
 * writes straight away, and one store refusing leaves the others saved and its own edits in place.
 */
export function StorePricesModal({ open, ...props }: StorePricesModalProps) {
  return open ? <Body {...props} /> : null;
}
